import React, { FC } from "react";
import { useForm } from "react-hook-form";
import { ensure, Form, FormActions, FormFields, FormTextField, Spinner, Stack } from "sprint-common-web";

import { CourseOfActionReference } from "../../../../../common/types/CourseOfActionReference";
import { FixedBillingReference } from "../../../../../common/types/FixedBillingReferfence";
import { CourseOfActionFormFields } from "./course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "./course-of-action/useCourseOfActionFormFieldsValues";

export interface FileRecordStatusDetailsFormInputs {
    courseOfActionId: string;
    fixedBillingId?: string;
    details: string;
}

export type FileRecordStatusDetailsFormProps = {
    onConfirm: ({
        courseOfAction,
        fixedBilling,
        details
    }: {
        details: string;
        courseOfAction: CourseOfActionReference;
        fixedBilling?: FixedBillingReference;
    }) => Promise<void>;
    onCancel: () => void;
};

export const FileRecordStatusDetailsForm: FC<FileRecordStatusDetailsFormProps> = ({
    onConfirm,
    onCancel
}: FileRecordStatusDetailsFormProps) => {
    const { control, errors, handleSubmit, formState, reset } = useForm<FileRecordStatusDetailsFormInputs>();
    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const handleCancel = () => {
        onCancel();
        reset();
    };

    const handleSubmitButtonClick = async (inputs: FileRecordStatusDetailsFormInputs) => {
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        ensure(courseOfAction);

        return onConfirm({
            courseOfAction,
            fixedBilling,
            details: inputs.details
        }).then(() => reset());
    };

    if (areCourseOfActionsLoading || areFixedBillingsLoading) {
        return (
            <Stack
                styles={{
                    root: { padding: 10 }
                }}
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleSubmitButtonClick)}>
                <FormFields>
                    <CourseOfActionFormFields
                        control={control}
                        errors={errors}
                        courseOfActions={courseOfActions}
                        fixedBillings={fixedBillings}
                    />
                    <FormTextField
                        control={control}
                        label="Status details"
                        name="details"
                        multiline={true}
                        errors={errors}
                    />
                </FormFields>
                <FormActions
                    isLoading={formState.isSubmitting}
                    submitDisabled={areCourseOfActionsLoading || areFixedBillingsLoading || formState.isSubmitting}
                    onCancel={handleCancel}
                />
            </Form>
        </Stack>
    );
};
